// =============================================================================
// Self-contained Code 128 (auto B/C) barcode -> inline SVG generator.
// Used to print scannable inventory tags on the Zebra label printer, faithful to
// the legacy POS rptXout / rptXout_Small reports which encoded the item `scan`
// number as a barcode. No external dependency so the build stays clean and the
// output is deterministic. The encoded value is what a register scanner reads
// back (see getProductByScan / useBarcodeScanner).
// =============================================================================

// Standard Code 128 bar/space width patterns for values 0..106 (106 = STOP).
const PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
]

const START_B = 104
const START_C = 105
const STOP = 106
const SHIFT_C = 99   // Code B -> switch to Code C
const SHIFT_B = 100  // Code C -> switch to Code B

const isDigit = (c: string) => c >= '0' && c <= '9'

// Convert a string into the list of Code 128 symbol values, auto-switching
// between Code B (full ASCII) and Code C (double-density numeric).
function encodeValues(text: string): number[] {
  const values: number[] = []
  const countDigits = (p: number) => {
    let n = 0
    while (p + n < text.length && isDigit(text[p + n])) n++
    return n
  }

  let pos = 0
  let mode: 'B' | 'C'
  if (text.length >= 2 && countDigits(0) >= 2) { mode = 'C'; values.push(START_C) }
  else { mode = 'B'; values.push(START_B) }

  while (pos < text.length) {
    if (mode === 'C') {
      if (countDigits(pos) >= 2) {
        values.push(parseInt(text.substr(pos, 2), 10))
        pos += 2
        if (countDigits(pos) < 2 && pos < text.length) { values.push(SHIFT_B); mode = 'B' }
      } else {
        values.push(SHIFT_B); mode = 'B'
      }
    } else {
      const d = countDigits(pos)
      if (d >= 4 || (d >= 2 && pos + d === text.length && d % 2 === 0)) {
        values.push(SHIFT_C); mode = 'C'
      } else {
        // Code B: printable ASCII 32..126 maps to value (code - 32).
        const code = text.charCodeAt(pos)
        values.push((code >= 32 && code <= 126 ? code : 63 /* '?' */) - 32)
        pos++
      }
    }
  }
  return values
}

function checksum(values: number[]): number {
  let sum = values[0]
  for (let k = 1; k < values.length; k++) sum += values[k] * k
  return sum % 103
}

export interface BarcodeOptions {
  module?: number   // width of a single bar module, px (default 2)
  height?: number   // bar height, px (default 60)
  quiet?: number    // quiet-zone width in modules each side (default 10)
}

// Render `value` as a Code 128 barcode SVG string (black bars on transparent).
export function code128Svg(value: string, opts: BarcodeOptions = {}): string {
  const { module = 2, height = 60, quiet = 10 } = opts
  const symbols = encodeValues(value)
  symbols.push(checksum(symbols))
  symbols.push(STOP)

  const pattern = symbols.map(v => PATTERNS[v]).join('')
  let x = quiet
  let bar = true
  const rects: string[] = []
  for (const ch of pattern) {
    const w = parseInt(ch, 10)
    if (bar) rects.push(`<rect x="${x * module}" y="0" width="${w * module}" height="${height}" fill="#000"/>`)
    x += w
    bar = !bar
  }
  const totalWidth = (x + quiet) * module
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${height}" viewBox="0 0 ${totalWidth} ${height}" preserveAspectRatio="none">${rects.join('')}</svg>`
}
